import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { ResponsiveContainer, ComposedChart, Area, Line, Bar, Rectangle, XAxis, YAxis, CartesianGrid, LabelList, Tooltip, ReferenceLine } from "recharts";

import { formatFull } from "./format";
import { axisTicks, formatAxis } from "./axisTicks";

/**
 * The goal card's hero: a running total across whatever window Basics is
 * pinned to (day / week / month / quarter / custom — same ScopeWindow every
 * scoped card already offers, see ComponentEditor's `def.scope` block), drawn
 * as a real line chart against a dashed goal line on the Y axis. Kelvin's
 * call: "it should be like a line graph... you decide on the X and Y axis...
 * JUST MAKE IT FLEXIBLE" — so the axis granularity adapts to however many
 * days the resolved window spans, rather than assuming "month."
 *
 * `view` decides what the series is:
 *
 *   • "cumulative" (the default, and what this card always did) — a RUNNING
 *     TOTAL of the metric's daily amounts, climbing toward the goal.
 *   • "daily" — each day on its own. Kelvin's ask (2026-09-28): the running
 *     total "accumulates data, then going higher and higher", with no way to
 *     see what a single day actually brought in.
 *
 * `goalLineMode` decides what it is measured against:
 *
 *   • "pace" (the default) — the goal spread over the period's WORKING days.
 *     Cumulative view draws the climbing expectation (flat over weekends,
 *     landing on the goal on the last working day); daily view draws the flat
 *     per-working-day figure. Either way it runs out to the END of the
 *     period, past today, which is what makes "behind, with nine days left"
 *     visible. See periodDaysFor in helpers/workingDays.js for the arithmetic.
 *   • "flat" — a single dashed rule at the goal itself, in both views (the
 *     daily view does NOT divide it into a per-day figure). Also the
 *     automatic fallback whenever there is no pace to draw (no goal, or a
 *     window with no working day in it).
 *
 * `days` decides which of them appear at all: every calendar day ("all", the
 * default) or only the ones the company trades on ("working" — Kelvin's ask,
 * 2026-09-28). Weekend and holiday money is still counted, it just isn't given
 * a column of its own to sit in.
 *
 * Sizing: Recharts' own ResponsiveContainer, the SAME library and pattern
 * the app's other card-filling line/area charts already use (TrendChart in
 * dashboards/Charts.jsx, behind "Metric charts" in the Add-component
 * drawer) — not hand-rolled SVG viewBox math. Three attempts at that each
 * broke a different way in the live app; this is the one technique already
 * proven, in this exact codebase, to resize cleanly with no distortion and
 * no blank state. It only needs what .gprog-chart already provides: an
 * ancestor with a resolved height (`flex: 1; min-height: 0`) — Recharts
 * measures that with its own ResizeObserver and repaints on every resize.
 *
 * Every render-prop below (GoalLabel/EndDot/ChartTip) is a MODULE-level
 * component, not one defined inside GoalTrendChart — a component declared
 * during render is a new type on every render to React, which tears down
 * and rebuilds its whole subtree each time (and fails the build's lint gate:
 * react/no-unstable-nested-components). Data they'd otherwise have closed
 * over comes in as explicit props instead.
 */
const fmtDate = (d) => moment(d, "YYYY-MM-DD").format("MMM D");

/* Recharts clones a React-element `label` with `viewBox` (the reference
   line's own x/y/width) — that's how the pill tracks the dashed line
   without any manual scale math on this end. */
const GoalLabel = ({ viewBox, text, goalColor, lineStartsAboveGoal, anchorRight }) => {
  if (!viewBox) return null;
  const pillW = text.length * 6.3 + 20;
  const pillH = 22;
  const x = anchorRight ? viewBox.x + (viewBox.width || 0) - pillW - 6 : viewBox.x + 6;
  /* Flip below whenever the pill's own corner would otherwise clip the card
     edge, OR the trend line is already running above the goal right where
     the pill sits (anchored at the chart's left edge — a goal reached
     early in the period, or just a small goal against a big number, puts
     the CLIMBING LINE exactly where "above the dashed line" used to always
     put the pill, so the two drew on top of each other). */
  const aboveY = viewBox.y - pillH - 7;
  const y = (aboveY < 4 || (lineStartsAboveGoal && !anchorRight)) ? viewBox.y + 9 : aboveY;
  return (
    <g>
      {/* Coloured from the SAME goalColor prop as the line itself, not a
          fixed CSS token — Kelvin's ask: "the goal pill should change also
          according to the goal line color." fillOpacity stands in for the
          old --gprog-goal-soft tint since an arbitrary admin-picked hex has
          no matching soft variant to reach for. */}
      {/* An opaque white backing first: the pill is drawn over bars / the
          area fill, and a tint alone let them show through the text. */}
      <rect x={x} y={y} width={pillW} height={pillH} rx={pillH / 2} fill="#fff" fillOpacity="0.82" />
      <rect x={x} y={y} width={pillW} height={pillH} rx={pillH / 2} fill={goalColor} fillOpacity="0.12" stroke={goalColor} strokeWidth="1" />
      <text x={x + pillW / 2} y={y + pillH / 2 + 4} textAnchor="middle" className="gprog-chart-goallabel" fill={goalColor}>{text}</text>
    </g>
  );
};
GoalLabel.propTypes = {
  viewBox: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number, width: PropTypes.number }),
  text: PropTypes.string,
  goalColor: PropTypes.string,
  lineStartsAboveGoal: PropTypes.bool,
  anchorRight: PropTypes.bool,
};
GoalLabel.defaultProps = { viewBox: null, text: "Goal", goalColor: "var(--gprog-goal)", lineStartsAboveGoal: false, anchorRight: false };

/* The pace line's own name, tucked in above its right-hand end where the flat
   rule's pill sits — the same EndLabel idiom ProfitTrend's pace line uses. A
   pill anchored at the LEFT edge (what GoalLabel does) would sit on top of a
   climbing pace line, which by definition starts down in the corner. */
const PaceEndLabel = ({ index, viewBox, capText, capFill, lastIndex }) => {
  if (index !== lastIndex || !viewBox) return null;
  return (
    <text
      x={viewBox.x - 4}
      y={viewBox.y - 16}
      textAnchor="end"
      fill={capFill}
      fontSize={10}
      fontWeight={600}
      /* A white halo behind the letters, so the caption reads over a bar
         without a box that would hide the bar. */
      stroke="#fff"
      strokeWidth={3}
      strokeLinejoin="round"
      paintOrder="stroke"
    >
      {capText}
    </text>
  );
};
PaceEndLabel.propTypes = {
  index: PropTypes.number,
  viewBox: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  capText: PropTypes.string.isRequired,
  capFill: PropTypes.string.isRequired,
  lastIndex: PropTypes.number.isRequired,
};
PaceEndLabel.defaultProps = { index: -1, viewBox: null };

/* No extra props threaded in via a wrapping arrow function (that inline
   closure trips the same "component defined during render" rule as a
   nested declaration would) — the last data point carries its own
   `isLast`/`lineColor`/`reached` fields instead, so this reads everything
   it needs straight off `payload` and can be passed as `dot={EndDot}`,
   a plain stable reference. */
const EndDot = ({ cx, cy, payload }) => {
  if (!payload?.isLast) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill="#fff" />
      <circle cx={cx} cy={cy} r={3.2} fill={payload.lineColor} className={`gprog-chart-dot${payload.reached ? " pulse" : ""}`} />
    </g>
  );
};
EndDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.shape({
    isLast: PropTypes.bool,
    lineColor: PropTypes.string,
    reached: PropTypes.bool,
  }),
};
EndDot.defaultProps = { cx: 0, cy: 0, payload: null };

/* Both numbers under the pointer, not just the series: on a day that is behind
   pace the whole point is the gap, and reading it off two lines by eye is
   exactly what the pace line was added to stop. A future day has no actual at
   all — it says so rather than showing a misleading 0. */
const ChartTip = ({ active, payload, label, kind, goalColor }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const hasValue = row.value != null;
  return (
    <div className="gprog-tip">
      <div className="gprog-tip-t">{fmtDate(label)}</div>
      <div className="gprog-tip-v">{hasValue ? formatFull(row.value, kind) : "Not yet"}</div>
      {row.ahead && <div className="gprog-tip-pace" style={{ color: "#98a2b3" }}>Upcoming, after this period</div>}
      {hasValue && row.done != null && (
        <div className="gprog-tip-split">
          <span><i style={{ background: row.doneColor }} />{`Now completed ${formatFull(row.done, kind)}`}</span>
          <span><i style={{ background: row.openColor }} />{`Still scheduled ${formatFull(Math.max(0, row.value - row.done), kind)}`}</span>
        </div>
      )}
      {hasValue && row.done != null && row.earned != null && (
        <div className="gprog-tip-earned" title="Revenue from jobs completed on this day, at what they billed. They may have been booked for another day, so this can differ from the bar.">
          {`Completed that day ${formatFull(row.earned, kind)}`}
        </div>
      )}
      {row.pace != null && (
        <div className="gprog-tip-pace" style={{ color: goalColor }}>
          {`Pace ${formatFull(row.pace, kind)}`}
        </div>
      )}
    </div>
  );
};
ChartTip.propTypes = {
  active: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.array,
  label: PropTypes.string,
  kind: PropTypes.oneOf(["$", "%", "#"]),
  goalColor: PropTypes.string,
};
ChartTip.defaultProps = { active: false, payload: [], label: "", kind: "#", goalColor: "var(--gprog-goal)" };

/* The two-tone bar (Total Scheduled Revenue): one bar the height of what was
   scheduled that day, split like a progress bar standing on end. The finished
   work sits at the BOTTOM in green and the part still to do stands on top of it
   in its own colour, so the eye lands on what is left. Drawn as ONE path
   clipped to itself rather than two stacked Bars, so the rounded top corners
   belong to whichever colour is on top and a day with nothing (or everything)
   completed is still a single clean bar. A module-level component reading
   everything off `payload` (not a closure) for the same reason EndDot is. */
/* How faded the look-ahead days are drawn against the period's own. */
const AHEAD_OPACITY = 0.5;

const SplitBar = ({ x, y, width, height, payload }) => {
  if (!(height > 0) || !(width > 0)) return null;
  const r = Math.min(4, width / 2, height);
  const path = `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
  const total = Number(payload?.value) || 0;
  const share = total > 0 ? Math.min(1, Math.max(0, (Number(payload?.done) || 0) / total)) : 0;
  const doneH = height * share;
  const clipId = `gprog-bar-${String(payload?.date || x).replace(/[^a-zA-Z0-9-]/g, "")}`;
  return (
    <g opacity={payload?.ahead ? AHEAD_OPACITY : 1}>
      <clipPath id={clipId}><path d={path} /></clipPath>
      <path d={path} fill={payload?.openColor} />
      {share > 0 && (
        <rect x={x} y={y + height - doneH} width={width} height={doneH} fill={payload?.doneColor} clipPath={`url(#${clipId})`} />
      )}
    </g>
  );
};
SplitBar.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  payload: PropTypes.shape({
    date: PropTypes.string,
    value: PropTypes.number,
    done: PropTypes.number,
    doneColor: PropTypes.string,
    openColor: PropTypes.string,
    ahead: PropTypes.bool,
  }),
};
SplitBar.defaultProps = { x: 0, y: 0, width: 0, height: 0, payload: null };

/* An ordinary bar that fades the look-ahead days. Only used when there are
   such days, so every other card keeps recharts' own bar untouched. */
const PlainBar = (props) => <Rectangle {...props} fillOpacity={props?.payload?.ahead ? AHEAD_OPACITY : 1} />;
PlainBar.propTypes = { payload: PropTypes.shape({ ahead: PropTypes.bool }) };
PlainBar.defaultProps = { payload: null };

export const SERIES_VIEWS = ["cumulative", "daily"];
export const GOAL_LINE_MODES = ["pace", "flat"];
export const DAY_SCOPES = ["all", "working"];

/**
 * The rows the chart plots, built from the metric's per-day amounts and the
 * period calendar — pure, and exported so the arithmetic is testable without
 * rendering recharts.
 *
 * Three decisions, in the order they have to happen:
 *
 *  1. Every day of the period gets its figure. The running total is summed
 *     over ALL of them, weekends included — a Saturday's takings are money the
 *     company made whether or not the axis goes on to show that Saturday.
 *  2. `withPace` decides how far the chart REACHES. With a pace line it runs
 *     to the period's end, so the actual series stops at today and the pace
 *     carries on alone; without one it stops where the figures do, which is
 *     the chart this card drew before any of this existed.
 *  3. `workingOnly` drops the days the company never planned to trade. Last,
 *     so hiding a Saturday cannot lose its money out of the running total.
 *
 * A day past today is `value: null`, never 0: zero claims nothing was earned
 * and would drag a cumulative line back down to the axis for the rest of the
 * month.
 */
export const buildChartRows = ({ series, periodDays, view, withPace, workingOnly, completedSeries, earnedSeries }) => {
  const byDate = new Map();
  (Array.isArray(series) ? series : []).forEach((p) => {
    if (p && p.date) byDate.set(String(p.date), Number(p.value) || 0);
  });
  /* The completed share of each day, for Total Scheduled Revenue only. Null
     (not an empty map) when the card has none, so `done` stays off every row
     and the chart draws plain bars. */
  const doneByDate = Array.isArray(completedSeries) ? new Map() : null;
  if (doneByDate) {
    completedSeries.forEach((p) => {
      if (p && p.date) doneByDate.set(String(p.date), Number(p.value) || 0);
    });
  }
  /* Revenue completed on each day, filed by completion rather than by the day
     a job was booked for. Rides along on the split bars' rows so the tooltip can
     set it beside the bar; null leaves `earned` off every row. */
  const earnedByDate = Array.isArray(earnedSeries) ? new Map() : null;
  if (earnedByDate) {
    earnedSeries.forEach((p) => {
      if (p && p.date) earnedByDate.set(String(p.date), Number(p.value) || 0);
    });
  }
  /* No calendar (no window resolved yet) — fall back to the days the metric
     itself reported, all of them past and all of them counted as trading, so
     an unpaced card still draws rather than blanking. */
  const calendar = Array.isArray(periodDays) && periodDays.length
    ? periodDays
    : (Array.isArray(series) ? series : []).map((p) => ({
      date: p.date, working: true, future: false, pace: null, cumPace: null,
    }));

  const isDaily = view === "daily";
  let running = 0;
  let runningDone = 0;
  let runningEarned = 0;
  const rows = calendar.map((row) => {
    const known = !row.future && byDate.has(String(row.date));
    const day = known ? byDate.get(String(row.date)) : null;
    if (day != null) running += day;
    let value = null;
    if (day != null) value = isDaily ? day : running;
    let pace = null;
    if (withPace) pace = isDaily ? row.pace : row.cumPace;
    const out = { date: row.date, working: row.working !== false, future: !!row.future, value, pace };
    if (row.ahead) out.ahead = true;
    if (doneByDate && day != null) {
      /* Never more than the bar it sits on: the two come from separate reads,
         and a completed figure a hair ahead of its day must not overflow it. */
      const dayDone = Math.min(day, doneByDate.get(String(row.date)) || 0);
      runningDone += dayDone;
      out.done = isDaily ? dayDone : Math.min(running, runningDone);
      if (earnedByDate) {
        runningEarned += earnedByDate.get(String(row.date)) || 0;
        out.earned = isDaily ? (earnedByDate.get(String(row.date)) || 0) : runningEarned;
      }
    }
    return out;
  });

  return rows.filter((r) => (withPace || !r.future) && (!workingOnly || r.working));
};

const SERIES_STYLES = ["area", "line", "bar", "spark"];
/* What of the chart's height isn't plot: the 30px top margin (room for the
   pace caption above its line) + the X axis's 30px band. */
const PLOT_CHROME_PX = 60;

const GoalTrendChart = ({
  series, completedSeries, earnedSeries, aheadDays, periodDays, dailyPace, goal, kind, color, goalColor, reached, style, view, goalLineMode,
  days, yStep, showGoalLine, showTooltip, additive,
}) => {
  const chartStyle = SERIES_STYLES.includes(style) ? style : "area";
  const isSpark = chartStyle === "spark";
  const lineColor = reached ? "var(--gprog-good)" : color;
  const chartView = SERIES_VIEWS.includes(view) ? view : "cumulative";
  const isDaily = chartView === "daily";
  const goalNum = Number(goal) || 0;
  const paceNum = Number(dailyPace) || 0;
  /* Pace needs a per-day figure to exist at all, and the admin has to have
     left the goal line on — switching the line off switches the pace off with
     it, because the pace IS this card's goal line now. */
  const withPace = showGoalLine
    && goalLineMode !== "flat"
    && paceNum > 0
    /* A rate is not earned a day at a time: "48% closed ÷ 22 working days" is
       2.2% of nothing. A percentage goal keeps the flat rule it always had —
       the same reason ProfitTrend drops its own pace line off a share-of-
       revenue axis. */
    && kind !== "%"
    /* Nor is a per-hour or per-day average: "$175 an hour ÷ 22 working days"
       is not what a day is supposed to bring. */
    && additive
    && Array.isArray(periodDays)
    && periodDays.some((d) => d.pace != null);
  /* Weekends and company holidays off the axis entirely — for a card whose
     metric only ever moves on a working day, they are a run of empty bars (or
     a flat stretch of line) between every week. The money earned on one still
     counts: it lands in the running total, and in the figure above the chart,
     exactly as before. */
  const workingOnly = days === "working";

  /* Two-tone bars: only for the bar style, and only when the card handed us a
     completed share. Everything else draws exactly as before. */
  const splitBars = chartStyle === "bar" && Array.isArray(completedSeries) && completedSeries.length > 0;

  const data = useMemo(() => {
    const rows = buildChartRows({
      series, periodDays, view: chartView, withPace, workingOnly, completedSeries: splitBars ? completedSeries : null,
      earnedSeries: splitBars ? earnedSeries : null,
    });
    if (splitBars) {
      /* Fixed colours, not the card's line colour: that one turns green once the
         goal is reached, which would erase the difference being drawn. */
      rows.forEach((r, i) => {
        if (r.done != null) rows[i] = { ...r, doneColor: "var(--gprog-good)", openColor: "var(--gprog-open)" };
      });
    }
    /* Look-ahead days: bars fade them in place, but a line or area has to
       change how it is DRAWN, so the period and the days after it are two
       series. The last real day is in both, so the dashed stretch starts where
       the solid one stops instead of leaving a gap. */
    if (rows.some((r) => r.ahead)) {
      let lastReal = -1;
      rows.forEach((r, i) => { if (!r.ahead && r.value != null) lastReal = i; });
      rows.forEach((r, i) => {
        rows[i] = {
          ...r,
          mainValue: r.ahead ? null : r.value,
          aheadValue: r.ahead || i === lastReal ? r.value : null,
        };
      });
    }
    /* The endpoint dot belongs on the last day that HAS a figure — with the
       chart drawn out to the end of the month, the last row is usually a
       future day with nothing on it. */
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i].value != null && !rows[i].ahead) {
        rows[i] = { ...rows[i], isLast: true, lineColor, reached };
        break;
      }
    }
    return rows;
  }, [series, completedSeries, earnedSeries, splitBars, periodDays, chartView, withPace, workingOnly, lineColor, reached]);

  /* The plot's own height, so a fixed Y step is drawn as-is for as many labels
     as actually fit, and only thinned past that (axisTicks.js). Recharts
     already measures the container; this just listens. */
  const [chartHeight, setChartHeight] = useState(0);

  const hasAhead = data.some((d) => d.ahead);
  const aheadStart = hasAhead ? data.find((d) => d.ahead) : null;
  /* The pace line stops at the period's end, so its caption belongs on the last
     day that still has a pace, not on the last row of the chart. */
  const lastPaceIndex = data.reduce((last, d, i) => (d.pace != null ? i : last), -1);
  const mainKey = hasAhead ? "mainValue" : "value";
  /* The bar's own drawing: the two-tone bar, else a plain one that fades the
     look-ahead days, else recharts' default. Plain ifs, not a nested ternary
     (the production build's lint treats that as an error). */
  let barShape;
  if (splitBars) barShape = SplitBar;
  else if (hasAhead) barShape = PlainBar;
  const realDays = data.filter((d) => d.value != null).length;
  /* Two real days is still the bar for a TREND, but a pace line drawn across
     the whole month is worth showing on day one — it is the only thing on the
     card that says what today was supposed to bring. */
  /* Bars are the exception: a trend needs two points to be a line, but a single
     bar is already the whole answer — "what did the one day so far bring in" —
     and Average Daily Revenue showed an apology on the first day of the month
     with $3,244 sitting right above it (Jonathan, 2026-10-01). */
  const needsTrend = chartStyle !== "bar";
  if (realDays < 1 || (needsTrend && (data.length < 2 || (realDays < 2 && !withPace)))) {
    return (
      <div className="gprog-chart-empty">
        Not enough days in this window yet to draw a trend — check back tomorrow, or widen the date range in Basics.
      </div>
    );
  }

  /* The flat rule: the goal itself in the cumulative view, the per-working-day
     figure in the daily view (a month's goal drawn against single days would
     flatten every bar onto the axis). Only ever drawn when the pace line is
     not — the two would say the same thing twice.

     Choosing "Flat target" is choosing the goal itself in either view: the
     admin asked for the number, not for it divided by working days, so the
     daily view draws it whole too (bars below it are simply short of it).

     A percentage is the exception both ways: 48% closed is what a SINGLE day
     has to hit as much as the month does, so the daily view keeps the goal
     itself rather than dividing it into per-day crumbs. */
  const flatIsPace = isDaily && kind !== "%" && additive && goalLineMode !== "flat";
  const flatLineValue = flatIsPace ? paceNum : goalNum;
  const showFlatLine = showGoalLine && !withPace && flatLineValue > 0;
  const flatLabel = flatIsPace
    ? `Pace · ${formatFull(flatLineValue, kind)}/day`
    : `Goal · ${formatFull(flatLineValue, kind)}`;
  const firstValue = data.find((d) => d.value != null)?.value ?? 0;
  const lineStartsAboveGoal = flatLineValue > 0 && firstValue >= flatLineValue;
  /* The axis has to know about whichever rule is drawn on it — recharts' own
     "auto" domain only looks at the plotted series, so a goal above the data's
     max (the normal case: it is what you are climbing TOWARD) rendered just
     outside the visible plot. The pace line IS a plotted series, so it is
     already counted. */
  const axisCeiling = showFlatLine ? flatLineValue : 0;
  /* Round labels (Auto) or the admin's own gap — see axisTicks.js. Worked out
     here rather than left to recharts, whose own ticks split an arbitrary
     domain evenly and printed $336,000 / $170,000 / $85,000. */
  const peak = data.reduce((m, d) => Math.max(m, d.value ?? 0, d.pace ?? 0), axisCeiling);
  const yAxis = axisTicks({ max: peak, step: yStep, plotHeight: chartHeight - PLOT_CHROME_PX });

  return (
    <div className="gprog-chart">
      <ResponsiveContainer width="100%" height="100%" onResize={(w, h) => setChartHeight(h)}>
        <ComposedChart data={data} margin={{ top: 30, right: 14, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="gprog-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity="0.28" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          {!isSpark && <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--gprog-border)" />}
          <XAxis
            dataKey="date"
            tickFormatter={fmtDate}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            tick={{ fontSize: 11, fill: "var(--gprog-faint)", fontWeight: 500 }}
            hide={isSpark}
            /* A category axis (implicit for a string dataKey) reserves a half-
               band of padding at each end by default, so the Area/Line/Bar's
               own rendered points never quite reach the plot's true edges —
               masked by the tick labels and margin most of the time, but the
               ReferenceLine spans the FULL pixel range regardless, so hiding
               everything else (Sparkline) exposed the gap between them as a
               visible seam on the right. */
            padding={{ left: 0, right: 0 }}
          />
          <YAxis
            tickFormatter={(v) => formatAxis(v, kind)}
            tickLine={false}
            axisLine={false}
            width={52}
            tickMargin={6}
            tick={{ fontSize: 11, fill: "var(--gprog-faint)", fontWeight: 500 }}
            /* Recharts' default domain is "auto" against the DATA series
               only — a ReferenceLine above the data's own max (the normal
               case: the goal is what you're climbing TOWARD) still rendered,
               just above the visible plot area. `peak` counts the goal, so
               the top label always clears it. interval 0: every computed
               tick is drawn — thinning already happened in axisTicks. */
            domain={[0, yAxis.top]}
            ticks={yAxis.ticks}
            interval={0}
            hide={isSpark}
          />
          {showTooltip && (
            <Tooltip content={<ChartTip kind={kind} goalColor={goalColor} />} cursor={{ stroke: lineColor, strokeOpacity: 0.25 }} />
          )}
          {chartStyle === "bar" && (
            <Bar
              dataKey="value"
              fill={lineColor}
              radius={[4, 4, 0, 0]}
              shape={barShape}
              /* With a day or two on the chart a bar would otherwise stretch
                 across the whole plot; capped, it reads as a bar. */
              maxBarSize={data.length < 3 ? 72 : undefined}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          )}
          {chartStyle === "line" && (
            <Line
              type="monotone"
              dataKey={mainKey}
              stroke={lineColor}
              strokeWidth={2.5}
              strokeLinecap="round"
              dot={EndDot}
              activeDot={{ r: 4, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
              connectNulls={!additive}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          )}
          {(chartStyle === "area" || isSpark) && (
            <Area
              type="monotone"
              dataKey={mainKey}
              stroke={lineColor}
              strokeWidth={2.5}
              strokeLinecap="round"
              fill="url(#gprog-area)"
              dot={isSpark ? false : EndDot}
              activeDot={{ r: 4, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
              connectNulls={!additive}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          )}
          {/* The look-ahead days on a line or area: the same line, dashed and
              lighter, carrying on from where the period's own stops. */}
          {hasAhead && (chartStyle === "line" || chartStyle === "area") && (
            <Line
              type="monotone"
              dataKey="aheadValue"
              stroke={lineColor}
              strokeOpacity={0.6}
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
            />
          )}
          {/* After the series, so the pace line is drawn ON TOP of the bars and
              the area fill — recharts paints in child order, and a line placed
              before them disappeared behind every bar it crossed. */}
          {/* Where we should be. Straight, never curved — it is a rule, not a
              trend, and a monotone spline through a weekend's flat stretch can
              still bow above the goal it is meant to land on. `connectNulls`
              so a window with no pace on some edge day cannot break it in
              two, and no dots: every point on it is the same statement. */}
          {withPace && (
            <Line
              type="linear"
              dataKey="pace"
              stroke={goalColor}
              strokeWidth={1.75}
              strokeDasharray="6 4"
              dot={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
              connectNulls
            >
              {!isSpark && (
                <LabelList
                  content={(
                    <PaceEndLabel
                      capText={isDaily ? `Pace ${formatFull(paceNum, kind)}/day` : "Pace"}
                      capFill={goalColor}
                      lastIndex={lastPaceIndex}
                    />
                  )}
                />
              )}
            </Line>
          )}
          {/* Where the period ends and the look-ahead begins. */}
          {aheadStart && !isSpark && (
            <ReferenceLine
              x={aheadStart.date}
              stroke="var(--gprog-faint)"
              strokeOpacity={0.6}
              strokeDasharray="3 3"
              label={{ value: `Next ${aheadDays || data.filter((d) => d.ahead).length} days`, position: "insideTopRight", fontSize: 10, fontWeight: 600, fill: "var(--gprog-faint)" }}
            />
          )}
          {/* Last, so the goal rule and its pill sit ON TOP of bars and the area
              fill instead of hiding behind them. */}
          {showFlatLine && (
            <ReferenceLine
              y={flatLineValue}
              className="gprog-chart-goalline"
              stroke={goalColor}
              strokeWidth={2.25}
              strokeLinecap="round"
              strokeDasharray="9 6"
              label={isSpark ? undefined : <GoalLabel text={flatLabel} goalColor={goalColor} lineStartsAboveGoal={lineStartsAboveGoal} anchorRight={chartStyle === "bar" || isDaily} />}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

GoalTrendChart.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    value: PropTypes.number,
  })),
  /* Total Scheduled Revenue only: per day, how much of the bar is completed
     work. Null / empty draws plain bars. */
  completedSeries: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    value: PropTypes.number,
  })),
  /* Total Scheduled Revenue only: Total Revenue per day by the day each job was
     completed — the tooltip's "Completed that day". Null leaves the line off. */
  earnedSeries: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    value: PropTypes.number,
  })),
  /* How many look-ahead days the calendar carries past the period (0 = none);
     the rows themselves are flagged `ahead` by periodDays. Used for the label. */
  aheadDays: PropTypes.number,
  /* One row per day from the period's start to its END (periodDaysFor in
     helpers/workingDays.js) — whether the company trades on it, whether it has
     happened yet, and what the goal expects by then. */
  periodDays: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    working: PropTypes.bool,
    future: PropTypes.bool,
    ahead: PropTypes.bool,
    pace: PropTypes.number,
    cumPace: PropTypes.number,
  })),
  dailyPace: PropTypes.number,
  goal: PropTypes.number,
  kind: PropTypes.oneOf(["$", "%", "#"]),
  color: PropTypes.string,
  goalColor: PropTypes.string,
  reached: PropTypes.bool,
  style: PropTypes.oneOf(SERIES_STYLES),
  view: PropTypes.oneOf(SERIES_VIEWS),
  goalLineMode: PropTypes.oneOf(GOAL_LINE_MODES),
  days: PropTypes.oneOf(DAY_SCOPES),
  /* The gap between Y-axis labels, in the metric's own units; null is Auto. */
  yStep: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showGoalLine: PropTypes.bool,
  showTooltip: PropTypes.bool,
  /* False for a rate / average: no pace line, no running total to speak of. */
  additive: PropTypes.bool,
};
GoalTrendChart.defaultProps = {
  series: [],
  completedSeries: null,
  earnedSeries: null,
  aheadDays: 0,
  periodDays: [],
  dailyPace: null,
  goal: 0,
  kind: "#",
  color: "var(--gprog-brand)",
  goalColor: "var(--gprog-goal)",
  reached: false,
  style: "area",
  view: "cumulative",
  goalLineMode: "pace",
  days: "all",
  yStep: null,
  showGoalLine: true,
  showTooltip: true,
  additive: true,
};

export default GoalTrendChart;
